import type { SubscriptionSetupStep } from './index';
import type { Instant } from '@js-joda/core';
import type { MutationObserverResult } from '@tanstack/query-core';
import type {
    CreateSubscriptionRequest,
    CreateSubscriptionResponse,
    SetupIntentResponse,
    SubscriptionPlan,
} from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { z } from 'zod';

export interface SubscriptionSetupState {
    currentStep: SubscriptionSetupStep;
    selectedPlanId: string | null;
    /** The plan the model picked for the user, so analytics can tell whether they kept it */
    autoSelectedPlanId: string | null;
    inviteCode: string;
    /** The trimmed invite code whose plans are being fetched */
    checkingInviteCode: string | null;
    basePlans: z.infer<typeof SubscriptionPlan>[];
    invitePlans: z.infer<typeof SubscriptionPlan>[];
    error: string | null;
    /** Stripe is confirming the submitted payment method; leaving now would abandon a payment in flight */
    confirmingPayment: boolean;
    awaitingActivation: boolean;
    cancelingSubscription: boolean;
    /**
     * Baseline `lastFailedAt` captured at the moment the user confirmed payment with Stripe.
     * `undefined` means we haven't submitted yet (no fast-polling for pending_payment_method).
     */
    knownLastFailedAt: Instant | null | undefined;
    /**
     * The latest Stripe payment submission for the pending subscription: its plan, which a slow Stripe
     * response is attributed to, and epoch ms, to measure how long activation takes
     */
    paymentSubmission: { planId: string; at: number } | null;
    existingPendingSubscriptionId: string | null;

    createSubscriptionResult: MutationObserverResult<
        z.infer<typeof CreateSubscriptionResponse>,
        Error,
        z.infer<typeof CreateSubscriptionRequest>,
        unknown
    > | null;
    setupIntentResult: MutationObserverResult<z.infer<typeof SetupIntentResponse>, Error, void, unknown> | null;
}

export type SubscriptionSetupAction =
    | { type: 'SET_INVITE_CODE'; inviteCode: string }
    | { type: 'INVITE_CODE_CHECK_STARTED'; inviteCode: string }
    | { type: 'INVITE_CODE_CHECK_FINISHED' }
    | { type: 'SELECT_PLAN'; planId: string; source: 'user' | 'auto' }
    | { type: 'CLEAR_SELECTED_PLAN' }
    | { type: 'GO_TO_STEP'; step: SubscriptionSetupStep }
    | { type: 'SET_ERROR'; error: string }
    | { type: 'CLEAR_ERROR' }
    | { type: 'FETCH_PLANS_ERROR'; error: string }
    | { type: 'SUBMIT_START' }
    | { type: 'SUBMIT_ERROR'; error: string }
    | { type: 'CANCEL_PENDING_START' }
    | { type: 'CANCEL_PENDING_SUCCESS' }
    | { type: 'CANCEL_PENDING_ERROR'; error: string }
    | { type: 'PAYMENT_SUBMITTED'; planId: string | null; at: number }
    | { type: 'PAYMENT_REJECTED' }
    | { type: 'PAYMENT_CONFIRMED'; knownLastFailedAt: Instant | null }
    | { type: 'SUBSCRIPTION_ACTIVATED' }
    | { type: 'PAYMENT_FAILED'; error: string }
    | {
          type: 'CREATE_SUBSCRIPTION_RESULT_UPDATED';
          result: SubscriptionSetupState['createSubscriptionResult'];
      }
    | {
          type: 'SETUP_INTENT_RESULT_UPDATED';
          result: SubscriptionSetupState['setupIntentResult'];
      }
    | { type: 'RESET_MUTATION_RESULTS' }
    | {
          type: 'RESET';
          pendingSubscription: { id: string; planId: string } | null;
      }
    | { type: 'SET_BASE_PLANS'; plans: z.infer<typeof SubscriptionPlan>[] }
    | { type: 'SET_INVITE_PLANS'; plans: z.infer<typeof SubscriptionPlan>[] };

export function createInitialState(): SubscriptionSetupState {
    return {
        currentStep: 'plan',
        selectedPlanId: null,
        autoSelectedPlanId: null,
        inviteCode: '',
        checkingInviteCode: null,
        basePlans: [],
        invitePlans: [],
        error: null,
        confirmingPayment: false,
        awaitingActivation: false,
        cancelingSubscription: false,
        knownLastFailedAt: undefined,
        paymentSubmission: null,
        existingPendingSubscriptionId: null,
        createSubscriptionResult: null,
        setupIntentResult: null,
    };
}

export function subscriptionSetupReducer(
    state: SubscriptionSetupState,
    action: SubscriptionSetupAction,
): SubscriptionSetupState {
    switch (action.type) {
        case 'SET_INVITE_CODE':
            return { ...state, inviteCode: action.inviteCode };

        case 'INVITE_CODE_CHECK_STARTED':
            return { ...state, checkingInviteCode: action.inviteCode };

        case 'INVITE_CODE_CHECK_FINISHED':
            return { ...state, checkingInviteCode: null };

        case 'SELECT_PLAN':
            return {
                ...state,
                selectedPlanId: action.planId,
                autoSelectedPlanId: action.source === 'auto' ? action.planId : state.autoSelectedPlanId,
            };

        case 'CLEAR_SELECTED_PLAN':
            return { ...state, selectedPlanId: null };

        case 'GO_TO_STEP':
            return { ...state, currentStep: action.step, error: null };

        case 'SET_ERROR':
            return { ...state, error: action.error };

        case 'CLEAR_ERROR':
            return { ...state, error: null };

        case 'FETCH_PLANS_ERROR':
            return { ...state, error: action.error };

        case 'SUBMIT_START':
            return { ...state, error: null };

        case 'SUBMIT_ERROR':
            return { ...state, error: action.error };

        case 'CANCEL_PENDING_START':
            return {
                ...state,
                createSubscriptionResult: null,
                setupIntentResult: null,
                existingPendingSubscriptionId: null,
                // The submission belonged to the subscription being canceled
                paymentSubmission: null,
                cancelingSubscription: true,
            };

        case 'CANCEL_PENDING_SUCCESS':
            return { ...state, cancelingSubscription: false };

        case 'CANCEL_PENDING_ERROR':
            return { ...state, cancelingSubscription: false, error: action.error };

        case 'PAYMENT_SUBMITTED':
            return {
                ...state,
                confirmingPayment: true,
                paymentSubmission: action.planId ? { planId: action.planId, at: action.at } : null,
            };

        case 'PAYMENT_REJECTED':
            return { ...state, confirmingPayment: false };

        case 'PAYMENT_CONFIRMED':
            return {
                ...state,
                confirmingPayment: false,
                knownLastFailedAt: action.knownLastFailedAt,
                awaitingActivation: true,
            };

        case 'SUBSCRIPTION_ACTIVATED':
            return {
                ...state,
                awaitingActivation: false,
                knownLastFailedAt: undefined,
                currentStep: 'complete',
                error: null,
            };

        case 'PAYMENT_FAILED':
            return {
                ...state,
                awaitingActivation: false,
                error: action.error,
            };

        case 'CREATE_SUBSCRIPTION_RESULT_UPDATED':
            return { ...state, createSubscriptionResult: action.result };

        case 'SETUP_INTENT_RESULT_UPDATED':
            return { ...state, setupIntentResult: action.result };

        case 'RESET_MUTATION_RESULTS':
            return {
                ...state,
                createSubscriptionResult: null,
                setupIntentResult: null,
            };

        case 'RESET': {
            const initial = createInitialState();
            if (action.pendingSubscription) {
                return {
                    ...initial,
                    basePlans: state.basePlans,
                    invitePlans: state.invitePlans,
                    existingPendingSubscriptionId: action.pendingSubscription.id,
                    selectedPlanId: action.pendingSubscription.planId,
                    currentStep: 'payment',
                };
            }
            return {
                ...initial,
                basePlans: state.basePlans,
                invitePlans: state.invitePlans,
            };
        }

        case 'SET_BASE_PLANS':
            return { ...state, basePlans: action.plans };

        case 'SET_INVITE_PLANS':
            return { ...state, invitePlans: action.plans };

        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
